import { Anchor, Box, Button, LoadingOverlay, Modal, Text, Title } from "@mantine/core"
import { ArrowLeftIcon, CheckIcon, FileTextIcon, WalletIcon, XIcon } from "@phosphor-icons/react"
import { ReactNode, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import useSwr from "swr"
import { InvoiceApi } from "../../apis"
import local from "../../components/buyer-invoice-details/BuyerInvoiceDetails.module.css"
import { InvoiceAttachmentsCard } from "../../components/buyer-invoice-details/invoice-attachments-card"
import { InvoiceLineItems } from "../../components/buyer-invoice-details/invoice-line-items"
import { ReceivingAccount } from "../../components/buyer-invoice-details/receiving-account"
import { BuyerInvoicePayForm } from "../../components/buyer-invoice-pay/buyer-invoice-pay-form"
import { AmendmentBanner } from "../../components/invoice-amendment/AmendmentBanner"
import { AmendmentDiff } from "../../components/invoice-amendment/AmendmentDiff"
import { AmendmentHistory } from "../../components/invoice-amendment/AmendmentHistory"
import { AmendmentRespondModal } from "../../components/invoice-amendment/AmendmentRespondModal"
import { handleAmendError } from "../../components/invoice-amendment/amend-error"
import { Page } from "../../components/layout/Page"
import { Breadcrumb } from "../../components/layout/breadcrumb"
import { usePermission } from "../../components/permission"
import { AppModalHeader } from "../../components/ui/app-modal"
import modalChrome from "../../components/ui/app-modal.module.css"
import { ReasonTooltip } from "../../components/ui/reason-tooltip"
import { StatusBadge, resolveInvoiceStatusColor } from "../../components/ui/status-badge"
import { usePdfDownload } from "../../hooks/usePdfDownload"
import { IGeneral } from "../../interfaces/IGeneral"
import { IReference } from "../../interfaces/IReference"
import { currencyFormat } from "../../utils"
import { message } from "../../utils/Message"
import { dateFormat, dateTimeFormat } from "../../utils/date"
import { daysUntilDue, dueDay } from "../../utils/due-day"
import { paymentTermLabel } from "../../utils/payment-term-label"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import classes from "../sales-invoice/details.module.css"
import { InvoicePaidHistory } from "./invoice-paid-history"

type StepState = "done" | "active" | "todo" | "stop"
type Step = { key: string; label: string; state: StepState }

type StepInput = {
  invoiceStatus?: string
  paymentStatus?: string
  grandTotal: number
  paid: number
  remaining: number
  paidPct: number
}

/**
 * Buyer «Явц»: Үүсгэсэн → Илгээсэн → Батлагдсан → Төлөгдсөн → Хаагдсан.
 * The active step is the one the invoice is waiting on (a SENT invoice waits on the
 * buyer's confirmation; a CONFIRMED one on payment). Payment statuses are
 * PENDING / DIVIDED / CLOSED (de-invoice-service INVOICE_PAYMENT_STATUS).
 */
function buyerSteps(input: StepInput, t: (key: string, opts?: Record<string, unknown>) => string): Step[] {
  const { invoiceStatus, paymentStatus, grandTotal, paid, remaining, paidPct } = input
  const label = {
    created: t("invoice:detail.stepper.draft"),
    sent: t("invoice:detail.stepper.sent"),
    confirmed: t("invoice:detail.stepper.confirmed"),
    paid: t("invoice:detail.stepper.paid"),
    closed: t("invoice:detail.stepper.closed"),
  }
  const keys = ["created", "sent", "confirmed", "paid", "closed"] as const
  const build = (doneUpTo: number, active?: { label: string; state: StepState }): Step[] =>
    keys.map((key, i) => {
      if (i < doneUpTo) return { key, label: label[key], state: "done" }
      if (i === doneUpTo && active) return { key, label: active.label, state: active.state }
      return { key, label: label[key], state: "todo" }
    })

  if (invoiceStatus === "CLOSED" || paymentStatus === "CLOSED") return build(keys.length)
  if (invoiceStatus === "REJECTED" || invoiceStatus === "CANCELED") {
    return build(2, { label: t(`invoice:invoiceStatus.${invoiceStatus}`), state: "stop" })
  }
  if (invoiceStatus === "CONFIRMED") {
    if (grandTotal > 0 && remaining <= 0) return build(4, { label: label.closed, state: "active" })
    return build(3, {
      label:
        paid > 0
          ? t("invoice:detail.stepper.payingPct", { pct: Math.round(paidPct) })
          : t("invoice:buyerDetail.stepAwaitingPayment"),
      state: "active",
    })
  }
  if (invoiceStatus === "SENT" || invoiceStatus === "VIEWED") {
    return build(2, { label: t("invoice:buyerDetail.stepAwaitingConfirm"), state: "active" })
  }
  return build(0, { label: label.created, state: "active" })
}

export const InvoiceDetails = () => {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["invoice", "common"])
  const [action, setAction] = useState<any[]>([])
  const [amendRejectOpened, setAmendRejectOpened] = useState(false)
  const [acceptingAmendment, setAcceptingAmendment] = useState(false)
  const payAutoOpened = useRef(false)

  const INV_PDF = usePermission("INV_PDF")
  const INV_PAY_WITH_QPAY = usePermission("INV_PAY_WITH_QPAY")
  const INV_PAY_WITH_B2B = usePermission("INV_PAY_WITH_B2B")
  const INV_ACCEPT_BUYER_PAYMENT = usePermission("INV_ACCEPT_BUYER_PAYMENT")
  const INV_DECLINE_BUYER_PAYMENT = usePermission("INV_DECLINE_BUYER_PAYMENT")
  const hasPayAccess = INV_PAY_WITH_B2B.hasAccess || INV_PAY_WITH_QPAY.hasAccess

  const invoiceStatusRef = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus)
  const paymentTermConditions = useSelector((state: { general: IGeneral }) => state.general.paymentTermConditions)

  const { data, error, isLoading, mutate } = useSwr<any>(
    params.id !== "new" ? `/api/invoice/${params.id}` : null,
    params.id !== "new" ? async () => await InvoiceApi.get(`${params.id}`) : null,
  )

  const { data: amendability, mutate: mutateAmendability } = useSwr(
    params.id ? `/api/invoice/${params.id}/amendability` : null,
    () => InvoiceApi.amendability(`${params.id}`),
  )

  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: InvoiceApi.pdf })

  // The list's pay action lands on /buyer-invoice/:id/pay: open the payment modal
  // once, as soon as the invoice is payable. Later reloads (after paying) must not
  // reopen it.
  useEffect(() => {
    if (payAutoOpened.current || params.action !== "pay") return
    if (data?.invoiceStatus === "CONFIRMED" && Number(data?.amountToPay ?? 0) > 0 && hasPayAccess) {
      payAutoOpened.current = true
      setAction(["pay", data])
    }
  }, [params.action, data, hasPayAccess])

  const onRespond = async (confirm: boolean) => {
    try {
      await InvoiceApi.respond(params.id || "", { confirm, respondText: "reject" })
      if (confirm) message.success(t("invoice:pay.approveSuccess"))
      else message.success(t("invoice:pay.rejectSuccess"))
      navigate("/buyer-invoice")
    } catch (error: any) {
      message.error(error.message)
    }
  }

  const handleModalSuccess = () => {
    setAction([])
    mutate()
  }

  if (isLoading) return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>

  // A 404 (not this business's invoice) or a failed request used to render the
  // whole page skeleton filled with dashes.
  if (error || !data) {
    return (
      <Page>
        <Box className={classes.card}>
          <Box className={classes.emptyInline}>
            <Text fz="sm" c="gray.7" mb="md">{error?.message || t("common:noData")}</Text>
            <Button variant="default" leftSection={<ArrowLeftIcon size={14} />} onClick={() => navigate("/buyer-invoice")}>
              {t("invoice:detail.back")}
            </Button>
          </Box>
        </Box>
      </Page>
    )
  }

  const d: any = data ?? {}

  const lines: any[] = Array.isArray(d.lines) ? d.lines : []
  const attachments: any[] = Array.isArray(d.attachments) ? d.attachments : []
  const additionalLines: any[] = Array.isArray(d.additionalLines) ? d.additionalLines : []

  const itemsTotal = Number(d.itemsTotal ?? 0)
  const vatAmount = Number(d.vatAmount ?? 0)
  const taxAmount = Number(d.taxAmount ?? 0)
  const shippingAmount = Number(d.shippingAmount ?? 0)
  const discountAmount = Number(d.discountAmount ?? 0)
  const additionalAmount = additionalLines.reduce((s, it) => s + Number(it?.quantity ?? 0) * Number(it?.price ?? 0), 0)
  const grandTotal = Number(d.totalAmount ?? d.confirmedAmount ?? 0)
  const addOns = taxAmount + additionalAmount + shippingAmount
  const exclusiveGuess = itemsTotal + vatAmount + addOns - discountAmount
  const inclusiveGuess = itemsTotal + addOns - discountAmount
  const vatIncluded = vatAmount > 0 && Math.abs(inclusiveGuess - grandTotal) < Math.abs(exclusiveGuess - grandTotal)
  const runningBeforeDiscount = itemsTotal + addOns + (vatIncluded ? 0 : vatAmount)
  const reconcileDiscount = Math.max(0, Math.round((runningBeforeDiscount - grandTotal) * 100) / 100)
  const discountPct = itemsTotal > 0 ? Math.round((reconcileDiscount / itemsTotal) * 1000) / 10 : 0
  const paid = Number(d.paidAmount ?? 0)
  const remaining = Number(d.amountToPay ?? Math.max(0, grandTotal - paid))
  const paidPct = grandTotal > 0 ? Math.min(100, Math.max(0, (paid / grandTotal) * 100)) : 0
  const paidPctRounded = Math.round(paidPct)

  // GET /inv/invoice/:id carries no remainingDays; count calendar days to the due
  // date here. Only a CONFIRMED invoice with something left to pay can be overdue --
  // the backend ages nothing else.
  const isPaidOff = d.paymentStatus === "CLOSED" || d.invoiceStatus === "CLOSED" || remaining <= 0
  const ages = d.invoiceStatus === "CONFIRMED" && !isPaidOff
  const dueDays: number | null = ages ? daysUntilDue(d.paymentDate) : null
  const isOverdue = dueDays != null && dueDays < 0

  const sender = d.senderBusiness ?? null
  const receiver = d.receiverBusiness ?? null

  const invStatusRef = invoiceStatusRef?.find((x: IReference) => x.code === d.invoiceStatus)
  const invStatusLabel =
    d.invoiceStatus === "SENT"
      ? t("invoice:buyerList.statusSent")
      : t(`invoice:invoiceStatus.${d.invoiceStatus}`, { defaultValue: invStatusRef?.name ?? d.invoiceStatus })
  const payPillLabel =
    d.paymentStatus === "CLOSED"
      ? t("invoice:detail.summaryCard.fullyPaid")
      : d.paymentStatus === "DIVIDED" || (paid > 0 && remaining > 0)
        ? t("invoice:buyerDetail.partiallyPaid")
        : null

  const isReview = d.invoiceStatus === "SENT" && params?.action === "review"
  const canRespond = INV_ACCEPT_BUYER_PAYMENT.hasAccess || INV_DECLINE_BUYER_PAYMENT.hasAccess
  const canPay = d.invoiceStatus === "CONFIRMED" && remaining > 0 && hasPayAccess
  const pending = amendability?.enabled ? amendability.pending : undefined

  const handleAcceptAmendment = async () => {
    if (!params.id || !amendability?.pending?.id) return
    try {
      setAcceptingAmendment(true)
      await InvoiceApi.respondAmendment(`${params.id}`, amendability.pending.id, { accept: true })
      message.success(t("invoice:amend.acceptedToast"))
      await Promise.all([mutate(), mutateAmendability()])
    } catch (err) {
      handleAmendError(err)
    } finally {
      setAcceptingAmendment(false)
    }
  }

  // Business carries no contact fields; the API nests them on the partner.
  const contactEmail = sender?.partner?.email ?? null
  const contactPhone = sender?.partner?.phone ?? null

  const withTtd = (business: any) => {
    const name = renderBusinessName(business, "-")
    const ttd = business?.regNumber ?? business?.partner?.regNumber
    return ttd ? `${name} · ${t("invoice:detail.meta.ttd")} ${ttd}` : name
  }

  const createdDate = d.createdAt ? dateFormat(d.createdAt) : null
  const confirmedDate = d.confirmedDate ? dateFormat(d.confirmedDate) : null
  const createdConfirmed =
    createdDate && confirmedDate && createdDate !== confirmedDate
      ? `${createdDate} / ${confirmedDate}`
      : (confirmedDate ?? createdDate ?? "-")
  const dueDate = dueDay(d.paymentDate)

  const metaItems: { key: string; label: string; value: ReactNode; mono?: boolean; danger?: boolean }[] = [
    { key: "sender", label: t("invoice:detail.meta.sender"), value: withTtd(sender) },
    { key: "receiver", label: t("invoice:detail.meta.receiver"), value: withTtd(receiver) },
    { key: "currency", label: t("invoice:detail.meta.currency"), value: "MNT" },
    { key: "created", label: t("invoice:detail.meta.createdConfirmed"), value: createdConfirmed },
    { key: "due", label: t("invoice:col.dueDate"), value: dueDate ?? "-", danger: isOverdue },
    {
      key: "terms",
      label: t("invoice:detail.meta.terms"),
      value: paymentTermLabel(
        d.paymentTerm ?? d.paymentTermName ?? null,
        paymentTermConditions,
        t("invoice:detail.meta.termsDefault"),
      ),
    },
    { key: "salesOrder", label: t("invoice:form.salesOrder"), value: d.orderSalesCode || "-", mono: !!d.orderSalesCode },
    {
      key: "purchaseOrder",
      label: t("invoice:form.purchaseOrder"),
      value: d.orderPurchaseCode || "-",
      mono: !!d.orderPurchaseCode,
    },
  ]

  // A 0 invoice is neither paid nor due; it only shows the percentage.
  const progressMeta =
    grandTotal <= 0 || isPaidOff
      ? t("invoice:detail.kpi.paidPctOnly", { pct: paidPctRounded })
      : d.paymentTermConfigType === "COD"
        ? t("invoice:detail.kpi.paidProgressOnReceipt", { pct: paidPctRounded })
      : dueDays == null
        ? t("invoice:detail.kpi.paidPctOnly", { pct: paidPctRounded })
        : dueDays < 0
          ? t("invoice:detail.kpi.paidProgressOverdue", { pct: paidPctRounded, days: Math.abs(dueDays) })
          : dueDays === 0
            ? t("invoice:detail.kpi.paidProgressToday", { pct: paidPctRounded })
            : t("invoice:detail.kpi.paidProgress", { pct: paidPctRounded, days: dueDays })

  const steps = buyerSteps(
    { invoiceStatus: d.invoiceStatus, paymentStatus: d.paymentStatus, grandTotal, paid, remaining, paidPct },
    t,
  )
  const proposalCode = pending?.refCode?.includes("-") ? pending.refCode.split("-").slice(-1)[0] : "A1"

  return (
    <Page>
      <Box className={classes.pageHdr}>
        <Box className={classes.pageHdrLeft}>
          <Breadcrumb
            data={[
              { label: t("invoice:breadcrumb.salesInvoice") },
              { label: t("invoice:detail.crumbOpen"), to: "/buyer-invoice" },
              { label: d.refCode ?? "-" },
            ]}
          />

          <Box className={classes.titleRow}>
            <Title order={1} className={classes.pageTitle}>
              {d.refCode ?? "-"}
            </Title>
            {d.invoiceStatus && (
              <StatusBadge
                color={resolveInvoiceStatusColor(d.invoiceStatus, invStatusRef?.color)}
                label={invStatusLabel}
              />
            )}
            {payPillLabel && (
              <Box component="span" className={classes.payPill}>
                {payPillLabel}
              </Box>
            )}
            {pending && (
              <Box component="span" className={`${classes.statusPill} ${classes.statusPillAmendPending}`}>
                <Box component="span" className={classes.statusPillDot} />
                {t("invoice:amend.pendingStatusPill")}
              </Box>
            )}
          </Box>

          <Text className={classes.pageSub}>
            {renderBusinessName(sender, "-")}
            {confirmedDate && <> · {t("invoice:detail.subtitleConfirmed", { date: confirmedDate })}</>}
            {dueDate && (
              <>
                {" · "}
                <Text component="span" inherit className={isOverdue ? classes.pageSubOverdue : undefined}>
                  {t("invoice:detail.subtitleDue", { date: dueDate })}
                </Text>
              </>
            )}
          </Text>
        </Box>

        <Box className={classes.pageHdrRight}>
          <ReasonTooltip reason={INV_PDF.reason}>
            <Button
              variant="default"
              classNames={{ root: classes.actionBtn }}
              leftSection={<FileTextIcon size={14} />}
              loading={isPdfLoading(`${params.id}`)}
              onClick={() => downloadPdf(`${params.id}`)}
            >
              {t("invoice:detail.actions.pdf")}
            </Button>
          </ReasonTooltip>
          {isReview && canRespond && (
            <>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<XIcon size={14} weight="bold" />}
                onClick={() => onRespond(false)}
              >
                {t("invoice:pay.reject")}
              </Button>
              <Button
                color="brand"
                classNames={{ root: classes.actionBtnPrimary }}
                leftSection={<CheckIcon size={14} weight="bold" />}
                onClick={() => onRespond(true)}
              >
                {t("invoice:pay.approve")}
              </Button>
            </>
          )}
          {canPay && (
            <Button
              color="brand"
              classNames={{ root: classes.actionBtnPrimary }}
              leftSection={<WalletIcon size={14} weight="bold" />}
              onClick={() => setAction(["pay", d])}
            >
              {t("invoice:pay.makePayment")}
            </Button>
          )}
        </Box>
      </Box>

      {pending && (
        <AmendmentBanner
          pending={pending}
          mode="buyer"
          paidAmount={paid}
          invoiceStatus={d.invoiceStatus}
          currentLines={lines}
          currentAdditionalLines={additionalLines}
          showAccept={INV_ACCEPT_BUYER_PAYMENT.hasAccess}
          accepting={acceptingAmendment}
          onAccept={handleAcceptAmendment}
          showReject={INV_DECLINE_BUYER_PAYMENT.hasAccess}
          onReject={() => setAmendRejectOpened(true)}
        />
      )}

      <Box className={classes.grid}>
        <Box className={classes.colMain}>
          {/* Amounts */}
          <Box className={`${classes.kpiCard} ${classes.orderKpi}`}>
            <Box className={classes.kpiGrid}>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.total")}</Text>
                <Text className={classes.kpiValue}>{currencyFormat(grandTotal)}</Text>
              </Box>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.paid")}</Text>
                <Text className={classes.kpiValuePaid}>{currencyFormat(paid)}</Text>
              </Box>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.remaining")}</Text>
                <Text className={`${classes.kpiValueRemain} ${remaining < 0 ? classes.kpiValueOverdue : ""}`}>
                  {currencyFormat(remaining)}
                </Text>
              </Box>
            </Box>
            <Box>
              <Box className={classes.kpiBar}>
                <Box className={classes.kpiBarFill} w={`${paidPct}%`} />
              </Box>
              <Text className={classes.kpiMeta} mt={6} c={isOverdue ? "red.8" : undefined}>
                {progressMeta}
              </Text>
            </Box>
          </Box>

          {pending && (
            <Box className={classes.orderItems}>
              <AmendmentDiff currentLines={lines} currentAdditionalLines={additionalLines} pending={pending} />
            </Box>
          )}

          {/* Line items */}
          <Box className={`${classes.card} ${classes.orderItems}`}>
            <Box className={classes.cardHead}>
              <Title order={2} className={classes.cardTitle}>
                {t("invoice:detail.itemsHead")}
              </Title>
              <Text className={classes.cardSub}>
                {t("invoice:detail.lineRows", { count: lines.length + additionalLines.length })}
              </Text>
            </Box>
            <InvoiceLineItems
              lines={lines}
              additionalLines={additionalLines}
              totals={{
                taxAmount,
                additionalAmount,
                shippingAmount,
                vatAmount,
                vatIncluded,
                discount: reconcileDiscount,
                discountPct,
                grandTotal,
              }}
            />
          </Box>

          {/* General info */}
          <Box className={`${classes.card} ${classes.cardPad} ${classes.orderInfo}`}>
            <Box component="dl" className={classes.metaDl}>
              {metaItems.map((item) => (
                <Box key={item.key} className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>
                    {item.label}
                  </Text>
                  <Text
                    component="dd"
                    className={`${classes.metaDd} ${item.mono ? classes.metaMono : ""} ${
                      item.value === "-" ? classes.metaDdMuted : ""
                    }`}
                    c={item.danger ? "red.8" : undefined}
                  >
                    {item.value}
                  </Text>
                </Box>
              ))}
              <ReceivingAccount account={d.senderAcc} />
            </Box>
          </Box>

          <InvoiceAttachmentsCard description={d.description} attachments={attachments} />

          {/* Paid history */}
          <Box className={`${classes.card} ${classes.orderAttach}`}>
            <Box className={classes.cardHead}>
              <Title order={2} className={classes.cardTitle}>
                {t("invoice:detail.payHistory")}
              </Title>
            </Box>
            <Box className={local.cardBody}>
              <InvoicePaidHistory data={d} />
            </Box>
          </Box>

          {amendability?.enabled && (amendability.history?.length ?? 0) > 0 && (
            <Box className={classes.orderAmendHist}>
              <AmendmentHistory items={amendability.history ?? []} />
            </Box>
          )}
        </Box>

        <Box className={classes.colSide}>
          {/* Progress */}
          <Box className={`${classes.card} ${classes.cardPadSide} ${classes.orderProgress}`}>
            <Title order={2} className={classes.cardTitle} mb={2}>
              {t("invoice:detail.progressTitle")}
            </Title>
            <Box className={classes.progressList}>
              {steps.map((step) => (
                <Box
                  key={step.key}
                  className={`${classes.progressRow} ${
                    step.state === "todo" ? classes.progressRowTodo : step.state === "done" ? "" : classes.progressRowActive
                  }`}
                >
                  <Box
                    className={`${classes.progressDot} ${
                      step.state === "done"
                        ? classes.progressDotDone
                        : step.state === "active"
                          ? classes.progressDotActive
                          : step.state === "stop"
                            ? local.progressDotStop
                            : classes.progressDotTodo
                    }`}
                  >
                    {step.state === "done" ? <CheckIcon size={11} weight="bold" /> : null}
                    {step.state === "stop" ? <XIcon size={11} weight="bold" /> : null}
                  </Box>
                  <Text component="span" fz={14} fw={step.state === "active" || step.state === "stop" ? 600 : 400}>
                    {step.label}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Contact (the supplier) */}
          <Box className={`${classes.card} ${classes.cardPadSide} ${classes.orderContact}`}>
            <Title order={2} className={classes.cardTitle} mb={4}>
              {t("invoice:detail.contactTitle")}
            </Title>
            <Box component="dl" className={classes.contactDl}>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.party.supplier")}</Text>
                <Text component="dd" className={classes.metaDd}>{withTtd(sender)}</Text>
              </Box>
              {contactEmail && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.email")}</Text>
                  <Text component="dd" className={classes.metaDd}>
                    <Anchor href={`mailto:${contactEmail}`} inherit c="gray.9">
                      {contactEmail}
                    </Anchor>
                  </Text>
                </Box>
              )}
              {contactPhone && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.phone")}</Text>
                  <Text component="dd" className={classes.metaDd}>
                    <Anchor href={`tel:${contactPhone}`} inherit c="gray.9">
                      {contactPhone}
                    </Anchor>
                  </Text>
                </Box>
              )}
              {!contactEmail && !contactPhone && <Text fz={14} c="gray.7">{t("common:noData")}</Text>}
            </Box>
          </Box>

          {/* History */}
          <Box className={`${classes.historyCard} ${classes.cardPadSide} ${classes.orderHistory}`}>
            <Title order={2} className={classes.cardTitle} mb={4}>
              {t("invoice:detail.history.title")}
            </Title>
            <Box className={classes.timeline}>
              {pending && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotAmendPending}`} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:amend.historyProposed", { ref: proposalCode })}</Text>
                    <Text className={classes.tlSub}>
                      {dateTimeFormat(pending.proposedAt)}
                      {" · "}
                      {t("invoice:amend.historyTotals", {
                        from: currencyFormat(pending.previousTotal, { withSymbol: false }),
                        to: currencyFormat(pending.newTotal, { withSymbol: false }),
                      })}
                    </Text>
                  </Box>
                </Box>
              )}
              {d.paymentStatus === "CLOSED" && d.paidDate && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotPay}`} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.paid")}</Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.paidDate)}</Text>
                  </Box>
                </Box>
              )}
              {d.paymentStatus !== "CLOSED" && paid > 0 && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotPay}`} />
                  <Box>
                    <Text className={classes.tlTitle}>
                      {t("invoice:detail.history.partialPaid", { amount: currencyFormat(paid) })}
                    </Text>
                    {d.paidDate && <Text className={classes.tlSub}>{dateTimeFormat(d.paidDate)}</Text>}
                  </Box>
                </Box>
              )}
              {(amendability?.history ?? []).map((h) => {
                const dotCls =
                  h.status === "REJECTED"
                    ? classes.tlDotAmendRejected
                    : h.status === "WITHDRAWN"
                      ? classes.tlDotAmendWithdrawn
                      : classes.tlDotAmend
                return (
                  <Box key={h.id} className={classes.tlItem}>
                    <Box className={`${classes.tlDot} ${dotCls}`} />
                    <Box>
                      <Text className={classes.tlTitle}>
                        {t(`invoice:amend.status.${h.status}`)} · {h.refCode}
                      </Text>
                      <Text className={classes.tlSub}>
                        {dateTimeFormat(h.respondedAt ?? h.proposedAt)}
                        {" · "}
                        {t("invoice:amend.historyTotals", {
                          from: currencyFormat(h.previousTotal, { withSymbol: false }),
                          to: currencyFormat(h.newTotal, { withSymbol: false }),
                        })}
                      </Text>
                    </Box>
                  </Box>
                )
              })}
              {d.confirmedDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.confirmed")}</Text>
                    <Text className={classes.tlSub}>
                      {[d.confirmedUser ? renderName(d.confirmedUser) : null, dateTimeFormat(d.confirmedDate)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </Box>
                </Box>
              )}
              {d.viewedDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.viewed")}</Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.viewedDate)}</Text>
                  </Box>
                </Box>
              )}
              {d.sentDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.sent")}</Text>
                    <Text className={classes.tlSub}>
                      {[d.senderUser ? renderName(d.senderUser) : null, dateTimeFormat(d.sentDate)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </Box>
                </Box>
              )}
              {(d.createdAt || (!d.sentDate && !d.confirmedDate)) && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.created")}</Text>
                    {d.createdAt && <Text className={classes.tlSub}>{dateTimeFormat(d.createdAt)}</Text>}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Modal
        withCloseButton={false}
        size="lg"
        opened={action[0] === "pay"}
        onClose={() => setAction([])}
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <AppModalHeader title={t("invoice:pay.modalTitle")} onClose={() => setAction([])} />
        <BuyerInvoicePayForm action={action} setAction={setAction} onSuccess={handleModalSuccess} />
      </Modal>

      {params.id && amendability?.pending?.id && (
        <AmendmentRespondModal
          opened={amendRejectOpened}
          onClose={() => setAmendRejectOpened(false)}
          invoiceId={`${params.id}`}
          amendmentId={amendability.pending.id}
          onSuccess={async () => {
            await Promise.all([mutate(), mutateAmendability()])
          }}
        />
      )}
    </Page>
  )
}
